import {Pagination} from "@/components/layouts/page/TableLayout/Pagination";

import {memo, useEffect, useRef, useState} from "react";

import sortIcon from "@images/table/table-sort.svg";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {Spinner} from "@ui/general/Spinner";
import {WarningData} from "@ui/general/WarningData";
import TableSelect from "@ui/inputs/TableSelect";
import {TableSort} from "@ui/inputs/TableSort";
import {useSelector} from "react-redux";
import {useGlobalFilter, useRowSelect, useSortBy, useTable} from "react-table";
import {v4 as uuidv4} from "uuid";

import styles from "./styles.module.scss";

const GlobalFilter = memo(({searchValue, onSearch, searchPlaceholder}) => {
  const [value, setValue] = useState(searchValue || "");

  useEffect(() => {
    setValue(searchValue || "");
  }, [searchValue]);

  const handleChange = (e) => {
    const newValue = e.target.value;

    setValue(newValue);
    onSearch(newValue);
  };

  return (
    <input
      className={styles.searchInput}
      value={value}
      onChange={handleChange}
      placeholder={searchPlaceholder}
    />
  );
});

const TableCell = memo(({cell, cellContent}) => {
  const spanRef = useRef(null);

  useEffect(() => {
    if (spanRef.current) {
      const textContent =
        spanRef.current.textContent || spanRef.current.innerText || "";

      spanRef.current.setAttribute("title", textContent);
    }
  }, [cellContent, cell.column.id]);

  return (
    <td
      {...cell.getCellProps()}
      key={cell.column.id}
      data-label={
        typeof cell.column.Header === "string" ? cell.column.Header : ""
      }
    >
      <RootDesc>
        <span ref={spanRef}>{cellContent}</span>
      </RootDesc>
    </td>
  );
});

export const TableLayout = (props) => {
  const {role} = useSelector((state) => state.common.candidate);
  const {isMobile} = useSelector((state) => state.common.settings);

  const {
    pagination = true,
    settings = true,
    serverStatus,
    columns,
    data,
    dataSort,
    selectSort,
    dataSize,
    selectSize,
    searchPlaceholder,
    searchValue,
    onSearch,
    totalPages,
    currentPage,
    toPage,
    setSelected,
    filterCheckbox,
    pageSize = true,
    search = true,
    minHeight = 606,
  } = props;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    state,
    selectedFlatRows,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: dataSort
          ? [{id: dataSort.value, desc: dataSort.direction === "desc"}]
          : [],
      },
      manualSortBy: true,
      disableSortRemove: true,
    },
    useGlobalFilter,
    useSortBy,
    useRowSelect,
  );

  const sortList = columns
    ? columns
        .filter((column) => column.accessor)
        .map((column) => ({
          name: column.Header,
          value: column.accessor,
        }))
    : [];

  // Add a ref to track previous selected rows
  const prevSelectedRowsRef = useRef([]);

  useEffect(() => {
    if (setSelected) {
      const selectedRows = selectedFlatRows.map((row) => row.original);

      // Compare with previous selected rows to avoid unnecessary updates
      const prevSelectedRows = prevSelectedRowsRef.current;
      const hasChanged =
        JSON.stringify(selectedRows) !== JSON.stringify(prevSelectedRows);

      if (hasChanged) {
        // Update the ref with current selection
        prevSelectedRowsRef.current = selectedRows;
        setSelected(selectedRows);
      }
    }
  }, [selectedFlatRows, setSelected]);

  useEffect(() => {
    if (state.sortBy && state.sortBy.length > 0) {
      const [{id: sortField, desc}] = state.sortBy;

      selectSort({value: sortField, direction: desc ? "desc" : "asc"});
    }
  }, [state.sortBy]);

  const errorData = !data || serverStatus === "error";

  return (
    <div
      className={styles.tableWrapper}
      style={{minHeight: `${minHeight}rem`}}
    >
      {settings && (
        <div className={styles.tableSettings}>
          {role !== "Representative" && search && (
            <div
              style={
                errorData
                  ? {
                      opacity: "0.5",
                      pointerEvents: "none",
                      marginRight: "auto",
                    }
                  : {opacity: "1", pointerEvents: "all", marginRight: "auto"}
              }
            >
              <GlobalFilter
                searchValue={!errorData ? searchValue : ""}
                onSearch={!errorData ? onSearch : undefined}
                searchPlaceholder={searchPlaceholder}
              />
            </div>
          )}

          {isMobile && pageSize && (
            <div
              style={
                errorData
                  ? {opacity: "0.5", pointerEvents: "none"}
                  : {opacity: "1", pointerEvents: "all"}
              }
            >
              <TableSort
                options={sortList}
                onSelect={!errorData ? selectSort : undefined}
                activeOption={dataSort}
              />
            </div>
          )}

          {filterCheckbox && (
            <div className={styles.filterCheckbox}>{filterCheckbox}</div>
          )}

          {!isMobile && pageSize && (
            <div
              style={
                errorData
                  ? {opacity: "0.5", pointerEvents: "none"}
                  : {opacity: "1", pointerEvents: "all"}
              }
            >
              <TableSelect
                options={[5, 10, 25, 50, 100]}
                onSelect={!errorData ? selectSize : undefined}
                activeOption={!errorData ? dataSize : 5}
              />
            </div>
          )}
        </div>
      )}

      {serverStatus === "loading" ? (
        <Spinner
          width={80}
          height={80}
        />
      ) : serverStatus === "error" ? (
        <WarningData type={"error"} />
      ) : data && data.length === 0 ? (
        <WarningData type={"empty"} />
      ) : (
        !errorData && (
          <table
            className={styles.table}
            {...getTableProps()}
          >
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr
                  {...headerGroup.getHeaderGroupProps()}
                  key={uuidv4()}
                >
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={column.id}
                      style={{
                        width: `${column.width}rem`,
                        cursor: `${
                          column.id === "selection" ? "default" : "pointer"
                        }`,
                      }}
                      className={
                        column.id.includes("empty") ? styles.emptyTd : ""
                      }
                      title={""}
                    >
                      <RootDesc>
                        <b>
                          {column.render("Header")}

                          {(column.accessor || column.id === "name") &&
                            column.id !== "actions" && (
                              <img
                                src={sortIcon}
                                alt={"sort"}
                              />
                            )}
                        </b>
                      </RootDesc>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);

                const hasEmptyCell = row.cells.some((cell) =>
                  cell.column.id.includes("empty"),
                );

                return (
                  <tr
                    {...row.getRowProps()}
                    key={row.id}
                    className={hasEmptyCell ? styles.emptyTd : ""}
                  >
                    {row.cells.map((cell) => {
                      const cellContent = cell.render("Cell");

                      return (
                        <TableCell
                          key={cell.column.id}
                          cell={cell}
                          cellContent={cellContent}
                        />
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )
      )}

      {!errorData && pagination && serverStatus !== "loading" && (
        <Pagination
          data={data}
          totalPages={totalPages}
          page={currentPage}
          toPage={toPage}
        />
      )}
    </div>
  );
};

export default TableLayout;
